import { useContext, useEffect, useState } from 'react';
import { Box, Chip, Stack, Typography } from '@mui/material';
import { PageOuterContainer } from '../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../presentation-components/display-elements-components/page-heading';
import { LoadingPage } from '../loading-page';
import { IHeadCell } from '../../../shared/interfaces/table-display.model';
import { TableDisplay } from '../../presentation-components/miscellaneous-components/table-display';
import { IInflationIndex } from '../../../shared/services/inflation-index/inflation-index.model';
import InflationIndexService from '../../../shared/services/inflation-index/inflation-index.service';
import { UserDetailsContext } from '../../../context/user-details/user-details';

const inflationIndexService = new InflationIndexService();

export const SettingsInflationIndicesPage = () => {
  const userDetailsContext = useContext(UserDetailsContext);
  const orgCurrencyCode = userDetailsContext.userDetails?.organisation?.currencyCode;

  const [indices, setIndices] = useState<IInflationIndex[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedIndexId, setSelectedIndexId] = useState<string | null>(null);
  const [selected, setSelected] = useState<string[]>([]);

  useEffect(() => {
    inflationIndexService.getAllActive()
      .then(data => {
        setIndices(data);
        // Auto-select the default index for the org's currency
        const defaultForOrg = data.find(i => i.currencyCode === orgCurrencyCode && i.isDefault);
        setSelectedIndexId(defaultForOrg?.id ?? data[0]?.id ?? null);
      })
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, [orgCurrencyCode]);

  const selectedIndex = indices.find(i => i.id === selectedIndexId) ?? null;

  const indexHeadCells: IHeadCell[] = [
    { id: 'name', label: 'Index', numeric: false, sortable: false },
    { id: 'currency', label: 'Currency', numeric: false, sortable: false },
    { id: 'source', label: 'Source', numeric: false, sortable: false },
    { id: 'isDefault', label: '', numeric: false, sortable: false },
  ];

  const indexRows = indices.map(index => ({
    id: index.id,
    name: index.name,
    currency: index.currencyCode,
    source: index.source,
    isDefault: index.isDefault ? <Chip label="Default" color="primary" size="small" /> : null,
  }));

  const valueHeadCells: IHeadCell[] = [
    { id: 'year', label: 'Year', numeric: false, sortable: false },
    { id: 'value', label: 'Index Value', numeric: true, sortable: false },
  ];

  const valueRows = (selectedIndex?.values ?? [])
    .slice()
    .sort((a, b) => a.year - b.year)
    .map(v => ({
      id: String(v.year),
      year: v.year,
      value: v.value.toFixed(2),
    }));

  return (
    <PageOuterContainer>
      <PageHeading
        title="Inflation Indices"
        includeBackButton={true}
        backButtonLink="/settings/company-settings/overview"
        backButtonText="Back to settings"
      />
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 640 }}>
        These inflation indices are used to adjust spend values onto the same price-year basis as the
        emission factor before calculating emissions. The default index for your organisation's currency
        ({orgCurrencyCode}) is applied automatically.
      </Typography>

      <LoadingPage pageReady={!isLoading}>
        {indices.length === 0 ? (
          <Typography color="text.secondary">No inflation indices have been configured yet. Contact your system administrator.</Typography>
        ) : (
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={3} alignItems="flex-start">
            <Box sx={{ minWidth: 280 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>Available Indices</Typography>
              <TableDisplay
                headCells={indexHeadCells}
                rows={indexRows}
                selected={selected}
                setSelected={setSelected}
                onRowClick={(id) => setSelectedIndexId(id)}
              />
            </Box>

            {selectedIndex && (
              <Box sx={{ flex: 1 }}>
                <Stack direction="row" spacing={1} alignItems="center" mb={1}>
                  <Typography variant="subtitle2" fontWeight={600}>{selectedIndex.name}</Typography>
                  {selectedIndex.isDefault && <Chip label="Default" color="primary" size="small" />}
                </Stack>
                <Stack direction="row" spacing={1} mb={2} flexWrap="wrap">
                  <Chip label={`Currency: ${selectedIndex.currencyCode}`} size="small" />
                  <Chip label={`Source: ${selectedIndex.source}`} size="small" />
                  {selectedIndex.sourceUrl && (
                    <Chip
                      label="View source"
                      size="small"
                      component="a"
                      href={selectedIndex.sourceUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      clickable
                    />
                  )}
                </Stack>
                <Typography variant="caption" color="text.secondary" display="block" mb={1}>
                  {valueRows.length} year values · Formula: ratio = index[factor year] ÷ index[emission year]
                </Typography>
                <TableDisplay
                  headCells={valueHeadCells}
                  rows={valueRows}
                  selected={[]}
                  setSelected={() => {}}
                />
              </Box>
            )}
          </Stack>
        )}
      </LoadingPage>
    </PageOuterContainer>
  );
};
